import { useEffect, useState } from 'react';
import {
  type AppVersion,
  BUILT_VERSION,
  formatAppVersion,
  resolveAppVersion,
} from '@/native/app-version';

/**
 * The build, in small print — so "which APK is on this phone?" is answerable without a shell or
 * a developer.
 *
 * Renders the compiled-in version immediately and replaces it with the *installed* one once the
 * shell answers, rather than rendering nothing while it waits: the two agree on a bundled build,
 * so a flicker between them is only possible on a remote-URL beta shell, where the change is the
 * information. `title` says which of the two is on screen for anyone who needs to be sure.
 */
export function AppVersionLabel({ className }: { className?: string }) {
  const [version, setVersion] = useState<AppVersion>(BUILT_VERSION);

  useEffect(() => {
    let cancelled = false;
    void resolveAppVersion().then((resolved) => {
      if (!cancelled) setVersion(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <p
      className={className ?? 'text-center text-xs text-muted-foreground'}
      title={
        version.installed
          ? 'Installed app version'
          : `Web build${version.commit === null ? '' : ` · ${version.commit}`}`
      }
      data-testid="app-version"
    >
      {formatAppVersion(version)}
    </p>
  );
}
